import BaseQuery from "@/stateManagement/BaseQuery";
import type { VipTierProgressState } from "@apps/rewards/modules/VipTiers/stores/RewardsVipTierProgressStore";
import RewardsVipTierProgressStore from "@apps/rewards/modules/VipTiers/stores/RewardsVipTierProgressStore";
import type { Observable } from "rxjs";
import { combineLatest, map } from "rxjs";
import { singleton } from "tsyringe";

import RewardsVipTiersQuery from "./RewardsVipTiersQuery";

@singleton()
class RewardsVipTierProgressQuery extends BaseQuery<VipTierProgressState> {
    constructor(
        protected store: RewardsVipTierProgressStore,
        private readonly vipTiersQuery: RewardsVipTiersQuery
    ) {
        super(store);
    }

    tierProgress$ = this.select((state) => state.tierProgress ?? 0);

    status$ = this.select((state) => state.status);

    public selectSinglePercentageProgress(): Observable<number> {
        return combineLatest([
            this.tierProgress$,
            this.vipTiersQuery.selectCustomerNextTier(),
        ]).pipe(
            map(([tierProgress, nextTier]) =>
                nextTier && tierProgress
                    ? Number(
                          (
                              (tierProgress / nextTier.conditionValue) *
                              100
                          ).toFixed(2)
                      )
                    : tierProgress
            )
        );
    }

    public selectTiersWithPercentageProgress(
        extendedTierList$: Observable<
            Array<{ title: string; value: number; id: number }>
        >
    ) {
        return combineLatest([this.tierProgress$, extendedTierList$]).pipe(
            map(([tierProgress, tierList]) =>
                tierList.map((tier, index) => {
                    const currentCondition = tier.value;
                    const nextCondition =
                        tierList[index + 1]?.value || currentCondition;
                    let percentageProgress = 0;

                    if (tierProgress >= nextCondition) {
                        percentageProgress = 100;
                    } else if (tierProgress < currentCondition) {
                        percentageProgress = 0;
                    } else {
                        percentageProgress =
                            ((tierProgress - currentCondition) /
                                (nextCondition - currentCondition)) *
                            100;
                    }

                    return {
                        ...tier,
                        percentageProgress: Number(
                            percentageProgress.toFixed(2)
                        ),
                    };
                })
            )
        );
    }
}

export default RewardsVipTierProgressQuery;
